import { useCallback, useState } from 'react';
import { useCxRuntime } from '@cx-web/feature-cx-runtime';
import { VIEW_SIGNUP } from '@cx-web/feature-myoc-login/store';
import {
  TRACKING_ACTIONS,
  TRACKING_CATEGORIES,
  TRACKING_EVENTS,
  TRACKING_LABELS,
} from '@cx-web/feature-tracking/constants';

const EMAIL_REGEX = /\S+@\S+\.\S+/;

// NGF-6334/NGF-6479: local UI state for the onboarding widget. The widget has a single path —
// USER_SIGNUP — so clicking the CTA fires the sign-up-container analytics event and opens the MyOC
// auth modal through the framework-agnostic CX runtime seam (NGF-6331); both apps/atlas (RR7) and
// apps/atlas-astro share this hook. The old newsletter form-submit path, its injected fetcher, and
// the newsletter_signup / primary-view variants were removed, so the only state left is the email
// input and its on-blur validation — two useState slices, no reducer/store.
export function useOnboarding() {
  // NGF-6334: read the framework seams through the CX runtime adapter. RR7 supplies these from its
  // provider tree; atlas-astro from the island adapter mounted via withCxRuntime.
  const runtime = useCxRuntime();
  const { openAtView } = runtime.useAuthModal();
  const { track } = runtime.useTracking();

  const [input, setInput] = useState({ isValid: false, value: '' });
  const [errorOnBlur, setErrorOnBlur] = useState(false);

  const handleInputChange = useCallback((value: string = '') => {
    const isValid = EMAIL_REGEX.test(value);
    setInput({ isValid, value });
    // Clear a previously-shown blur error as soon as the input becomes valid again.
    if (isValid) setErrorOnBlur(false);
  }, []);

  const handleInputBlur = useCallback(() => {
    setErrorOnBlur(!input.isValid);
  }, [input.isValid]);

  const handleSubmit = useCallback(() => {
    track(TRACKING_EVENTS.SIGN_UP_CONTAINER, {
      eventAction: TRACKING_ACTIONS.signupContainer,
      eventCategory: TRACKING_CATEGORIES.ODDSCHECKER_ACCOUNT,
      eventLabel: TRACKING_LABELS.signupContainer,
    });
    // Open the signup view through the framework-agnostic auth-modal seam (NGF-6331). The adapter
    // routes this to the OC widget: RR7 via useAuthModal().openAtView, atlas-astro via the published
    // store. The seam is the modal's general openAtView; this caller chooses the signup view.
    openAtView(VIEW_SIGNUP, { prefillEmail: input.value });
  }, [input.value, openAtView, track]);

  return {
    disabled: !input.isValid,
    errorOnBlur,
    input,
    inputIsValid: input.isValid,
    handleInputChange,
    handleInputBlur,
    handleSubmit,
  };
}
